import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NgForm} from '@angular/forms';
import {first, finalize} from 'rxjs/operators';
import {ContratService} from '@services/admin/contrat/contrat.service';
import {ContratButtonService} from '@services/admin/contrat/contrat.button.service';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {TypeAction} from '@share/component/floating-button/floating-button';
import {ConfirmService} from '@share/component/confirmation/confirm.service';
import {TypeCodeErreur} from '@domain/common/http/result';
import {Compte, TypeCarte, TypeCartePaiement} from '@domain/contrat/compte';
import {TypeDebit} from '@domain/contrat/contrat';
import {User} from '@domain/user/user';
import {OptionCompte} from "@domain/admin/comptabilite/optionsTransverses";

/**
 * Composant de gestion d'une carte
 *
 * @author Yannick Gauthier
 * @date 21/08/2025
 */
@Component({
    host: {'data-test-id': 'contrat-compte'},
    selector: 'contrat-compte',
    templateUrl: './contrat-compte.component.html'
})
export class ContratCompteComponent implements OnInit, OnDestroy{
    /** Référence au formulaire */
    @ViewChild('compteForm', {static: false}) compteForm: NgForm;
    
    /** Carte */
    compte: Compte = new Compte(null);
    
    /** ID du contrat */
    idContrat: number;
    
    /** ID du fournisseur */
    idFournisseur: number;
    
    /** ID de la carte */
    idCarte: string;
    
    /** Indique si on est en création ou en édition */
    isCreation: boolean = true;
    
    /** Indique si une sauvegarde est en cours */
    isSaving: boolean = false;

    /** Liste des comptes tiers */
    listeCompteTiers: Array<OptionCompte> = [];

    /** Liste des comptes bancaires */
    listeCompteBancaire: Array<OptionCompte> = [];

    /** Indique si le contenu est chargé */
    contenuCharge: boolean = false;

    /** Liste des types de débit */
    listeTypesDebit: Array<{type: TypeDebit, libelle: string}> = [
        {type: TypeDebit.SOCIETE, libelle: this.translateService.instant('admin.frais.fournisseurs.contrat.type.societeCarte')},
        {type: TypeDebit.COLLAB, libelle: this.translateService.instant('admin.frais.fournisseurs.contrat.type.collabCarte')}
    ];

    /** Collaborateur sélectionné */
    collaborateur: User = null;
    
    /** Utilisateur sélectionné via le composant autocomplete */
    user: User = null;

    /** Énumération TypeCarte pour utilisation dans le template */
    TypeCarte = TypeCarte;

    /** Énumération TypeDebit pour utilisation dans le template */
    TypeDebit = TypeDebit;

    /**
     * Constructeur
     *
     * @param activatedRoute Route activée
     * @param router Service de routage
     * @param contratService Service de gestion des contrats
     * @param contratButtonService Service de gestion des boutons
     * @param toastrService Service de notification
     * @param translateService Service de traduction
     * @param confirmService Service de confirmation
     */
    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private contratService: ContratService,
        private contratButtonService: ContratButtonService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
        private confirmService: ConfirmService
    ) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération des paramètres de la route
        this.idContrat = +this.activatedRoute.parent.snapshot.params.idContrat;
        this.idCarte = this.activatedRoute.snapshot.params.idCarte;
        this.idFournisseur = +this.activatedRoute.parent.snapshot.queryParams.idFournisseur;

        //Détermination du mode (création ou édition)
        this.isCreation = !this.idCarte || this.idCarte === '0';

        // Mise à jour des actions avec la visibilité correcte du bouton supprimer
        this.initActions();
        
        //Chargement de la carte
        this.loadCarte();
        
        //Enregistrement de la fonction goBack dans le service
        this.contratButtonService.setCustomGoBackFunction(() => this.goBack());
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy(): void {
        
        //Réinitialisation de la fonction goBack personnalisée
        this.contratButtonService.resetCustomGoBackFunction();
    }

    /**
     * Charge les données de la carte
     */
    loadCarte(): void {
        this.contratService.getCarte(this.idContrat, this.idCarte).pipe(first()).subscribe({
            next: result => {
                //Vérification du code retour
                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                    //Vérification des comptes
                    if (result.data.listeCompte && result.data.listeCompteBanque) {
                        //Définition des comptes tiers
                        this.listeCompteTiers = result.data.listeCompte.map(compte => ({
                            ...compte,
                            libelle: `${compte.numero} - ${compte.libelle}`
                        }));
                        
                        //Définition des comptes bancaires
                        this.listeCompteBancaire = result.data.listeCompteBanque.map(compteBancaire => ({
                            ...compteBancaire,
                            libelle: `${compteBancaire.numero} - ${compteBancaire.libelle}`
                        }));
                    }

                    //Vérification de la carte
                    if (result.data.carte) {
                        //Définition de la carte 
                        this.compte = result.data.carte;

                        //Vérification de la date
                        if (!this.compte.dateFin) {
                            //Définition de la date par défaut
                            this.compte.dateFin = null;
                        }
                    } else if (this.isCreation) {
                        //Création d'une carte vierge
                        this.compte = new Compte(null);
                        this.compte.idContrat = this.idContrat;
                        this.compte.actif = true;
                        this.compte.typeDebit = TypeDebit.SOCIETE;
                        this.compte.typeCarte = TypeCarte.AFFAIRE;
                        this.compte.typeCartePaiement = TypeCartePaiement.INDETERMINE;
                    }

                    //Indique que le contenu est chargé
                    this.contenuCharge = true;

                } else {
                    //Message d'erreur
                    this.toastrService.error(
                        this.translateService.instant('admin.frais.contrat.error.load'),
                        this.translateService.instant('global.error.title')
                    );
                }
            },
            error: () => {
                //Message d'erreur
                this.toastrService.error(
                    this.translateService.instant('admin.frais.contrat.error.load'),
                    this.translateService.instant('global.error.title')
                );
            }
        });
    }

    /**
     * Initialisation des actions
     */
    initActions(): void {
        this.contratButtonService.setActions([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'global.actions.enregistrer',
                isVisible: () => !this.compteForm || this.compteForm.valid,
                doAction: () => this.saveCarte()
            },
            {
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                isVisible: () => !this.isCreation,
                doAction: () => this.deleteCarte()
            }
        ]);
    }

    /**
     * Sauvegarde la carte
     */
    saveCarte(): void {
        //Vérification de la validité du formulaire
        if (!this.compteForm.valid) {
            return;
        }
        
        //Mise à jour l'indicateur informant que le traitement est en cours
        this.isSaving = true;
        
        //Définition de l'identifiant du contrat
        this.compte.idContrat = this.idContrat;
        
        //Vérification du mode
        if (this.isCreation && this.compte.typeCarte === TypeCarte.AFFAIRE) {
            //Définition du numéro de carte en clair
            this.compte.numCarteClair = this.compte.idCarte.replace(/^(.{4})(.{8})/g, '$1XXXXXXXX');
        }
        
        //Mise à jour de l'utilisateur associé à la carte
        if (this.compte.user) {
            this.compte.idUser = this.compte.user.idUser;
        }
        
        //Sauvegarde de la carte
        this.contratService.saveCarte(this.compte, this.isCreation)
            .pipe(first(), finalize(() => {
                //Mise à jour l'indicateur informant que le traitement est terminé
                this.isSaving = false;
            }))
            .subscribe({
                next: result => {
                    //S'il n'y a pas d'erreur
                    if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //Message d'information
                        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                        
                        //Redirection vers la liste des comptes
                        this.goBack();
                    } else if (result.codeErreur === 6) {
                        //Message d'erreur
                        this.toastrService.error(this.translateService.instant('admin.frais.contrat.error.duplicateCard'));
                    } else {
                        //Message d'erreur
                        TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                    }
                },
                error: () => {
                    //Message d'erreur
                    this.toastrService.error(this.translateService.instant('global.erreurs.enregistrement'));
                }
            });
    }
    
    /**
     * Supprime la carte
     */
    deleteCarte(): void {
        //Mise à jour l'indicateur informant que le traitement est en cours
        this.isSaving = true;
        
        //Affichage de la confirmation de suppression de la carte
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(first())
            .subscribe(isConfirmed => {
                //Si l'utilisateur a confirmé
                if (isConfirmed) {
                    //Suppression de la carte
                    this.contratService.deleteCarte(this.compte)
                        .pipe(first(), finalize(() => {
                            //Mise à jour l'indicateur informant que le traitement est terminé
                            this.isSaving = false;
                        }))
                        .subscribe({
                            next: result => {
                                //S'il n'y a pas d'erreur
                                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                                    //Message d'information
                                    this.toastrService.success(this.translateService.instant('global.success.suppression'));
                                    
                                    //Redirection vers la liste des comptes
                                    this.goBack();
                                } else {
                                    //Message d'erreur
                                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                                }
                            },
                            error: () => {
                                //Message d'erreur
                                this.toastrService.error(this.translateService.instant('global.erreurs.suppression'));
                            }
                        });
                } else {
                    //Mise à jour l'indicateur informant que le traitement est terminé
                    this.isSaving = false;
                }
            });
    }

    /**
     * Retour à la liste des comptes
     */
    goBack(): void {
        //Navigation explicite vers l'onglet Comptes du contrat
        this.router.navigate(['Admin/Frais/Factures/Contrat', this.idContrat, 'Comptes'], 
            { queryParams: { idFournisseur: this.idFournisseur } });
    }
}
